import React, { useEffect, useState } from "react";
import { IoChevronDownOutline, IoListOutline } from "react-icons/io5";
import { getGroupTypeLabel } from "../../../i18n/groupTypes";
import { Button, IconButton } from "../../../components/ui";

/**
 * Табличное представление плана лечения.
 *
 * Иерархия: план → группа (направление) → услуга → приём. Для каждой группы
 * рисуется своя таблица: строки — услуги (и их состав), столбцы — приёмы
 * (для групп без приёмов — состав услуги), снизу — итоги и оплата по группе.
 *
 * Компонент ничего не запрашивает сам: данные, хелперы и обработчики приходят
 * пропсами из `PatientPlan` (там живут состояние, запросы и модальные окна).
 * Это разделяет разметку таблицы и схемы-матрицы: правки таблицы не задевают
 * `PatientPlanMatrix`.
 */
export default function PatientPlanTable({
    groups = [],
    changed = false,
    blocked = false,
    coursesMode = false,
    courseSelected = [],
    addServiceMenuGroupId = null,
    setAddServiceMenuGroupId,
    courseIdToColor = {},
    courseIdToName = {},
    displayName,
    getGroupEffectiveTotal,
    getGroupAppointmentLimit,
    getAppointmentHeaderType,
    getAppointmentHeaderLabels,
    isWithoutAppointmentsGroupByName,
    getMarkedAppointmentsCountForItem,
    getAppointmentForItem,
    renderAppointmentStatus,
    fetchPlan,
    isSuccessResponse,
    logPlanChange,
    onDecrease,
    onIncrease,
    onColumnTypeChange,
    onDeleteSubitem,
    onChangeService,
    onAddSubitem,
    onDeleteService,
    onRemoveFromCourse,
    onToggleCourse,
    onOpenAppointmentComment,
    onOpenPay,
    onDeleteGroup,
    onRequestAddGroup,
    onAddGroup,
    onOpenAddCourse,
    onOpenAddServiceFromCatalog,
    onOpenAddCustomService,
    onDeletePlan,
    deletePlanLabel,
    viewSwitch = null,
    actions = null,
    metaEditor = null,
    planTitle = "",
    planId = 0,
    t,
    locale,
}) {
    // Меню «Добавить группу»: раскрывается самой кнопкой (стрелка вниз),
    // без отдельного модального окна. Список направлений грузится при
    // первом открытии — через onRequestAddGroup.
    const [groupMenuOpen, setGroupMenuOpen] = useState(false);
    const [groupOptions, setGroupOptions] = useState([]);
    const [groupsLoading, setGroupsLoading] = useState(false);

    const closeGroupMenu = () => setGroupMenuOpen(false);

    const toggleGroupMenu = async () => {
        if (groupMenuOpen) {
            closeGroupMenu();
            return;
        }
        setGroupMenuOpen(true);
        if (!onRequestAddGroup) return;
        setGroupsLoading(true);
        try {
            const list = await onRequestAddGroup();
            setGroupOptions(Array.isArray(list) ? list : []);
        } finally {
            setGroupsLoading(false);
        }
    };

    const handlePickGroup = (option) => {
        closeGroupMenu();
        onAddGroup?.(option);
    };

    // Закрытие меню по клику вне кнопки и по Escape.
    useEffect(() => {
        if (!groupMenuOpen) return undefined;
        const handleDown = (event) => {
            if (!event.target.closest(".plan-group-add")) closeGroupMenu();
        };
        const handleKey = (event) => {
            if (event.key === "Escape") closeGroupMenu();
        };
        document.addEventListener("mousedown", handleDown);
        document.addEventListener("keydown", handleKey);
        return () => {
            document.removeEventListener("mousedown", handleDown);
            document.removeEventListener("keydown", handleKey);
        };
    }, [groupMenuOpen]);

    return (
        /* Все группы живут в ОДНОЙ карточке (.page-block): общая шапка
           с заголовком и кнопками, ниже — секции групп с разделителями,
           внизу — подвал «Добавить группу / Удалить план». */
        <div className="page-block patient-plan-card">
            <div className="card__head plan-matrix-head plan-card__head">
                <div className="plan-matrix-head__title">
                    <div className="card__title plan-matrix-title">
                        <IoListOutline size={19} aria-hidden="true" />
                        {planTitle || t("plan.table.title")}
                    </div>
                    <div className="card__sub plan-matrix-sub">
                        {t("plan.table.sub")}
                    </div>
                </div>
                <div className="plan-matrix-head__switch">{viewSwitch}</div>
                <div className="plan-matrix-head__actions">{actions}</div>
            </div>
            {/* Редактируемые поля плана (статус, дата начала) — только
                в режиме редактирования, сразу под шапкой карточки. */}
            {metaEditor ? (
                <div className="plan-card__meta">{metaEditor}</div>
            ) : null}
            {!groups.length ? (
                <div className="pd-empty">{t("plan.list.empty")}</div>
            ) : null}
            <div className="plan-groups">
                {groups.map((group) => {
                    const groupEffectiveTotal = getGroupEffectiveTotal(group);
                    const groupAppointmentLimit = getGroupAppointmentLimit(group);
                    const appointmentHeaderType = getAppointmentHeaderType(group);
                    const groupAppointmentNumbers = Array.from(
                        { length: groupAppointmentLimit },
                        (_, i) => i + 1,
                    );
                    const isWithoutAppointmentsGroup = isWithoutAppointmentsGroupByName(
                        group?.name?.value,
                    );
                    const showCourseColumn = coursesMode && !isWithoutAppointmentsGroup;
                    const showAppointmentColumns = !isWithoutAppointmentsGroup;

                    return (
                        <div key={group?.id?.value} className="group-section">
                            {/* Панель группы в режиме редактирования: счётчик
                                приёмов и режим подписи столбцов — в одну строку. */}
                            {changed && showAppointmentColumns && (
                                <div className="plan-group-toolbar">
                                    <div className="plan-count_appointment">
                                        <div className="plan-name-count_appointment">
                                            {t("plan.group.appointmentCount")}
                                        </div>
                                        <div className="plan-count_appointment-value">
                                            <button
                                                type="button"
                                                aria-label="-"
                                                className="plan-count_appointment-btn"
                                                onClick={() => onDecrease(group)}
                                                disabled={blocked}>
                                                −
                                            </button>
                                            <div className="plan-result-count_appointment">
                                                {groupAppointmentLimit ? groupAppointmentLimit : ""}
                                            </div>
                                            <button
                                                type="button"
                                                aria-label="+"
                                                className="plan-count_appointment-btn"
                                                onClick={() => onIncrease(group)}
                                                disabled={blocked}>
                                                +
                                            </button>
                                        </div>
                                    </div>
                                    <select
                                        className="plan-column-type-select"
                                        value={appointmentHeaderType}
                                        disabled={blocked || !changed}
                                        onChange={(event) =>
                                            onColumnTypeChange(group, event.target.value)
                                        }>
                                        <option value={1}>{t("plan.group.colTypeAppointments")}</option>
                                        <option value={2}>{t("plan.group.colTypeConsecutiveDays")}</option>
                                        <option value={3}>{t("plan.group.colTypeEvenDays")}</option>
                                        <option value={4}>{t("plan.group.colTypeOddDays")}</option>
                                    </select>
                                </div>
                            )}
                            {/* Таблица группы */}
                            {group?.items?.length > 0 ? (
                                <div className="table-wrapper">
                                    <div className="group-table">
                                        <table>
                                            <thead>
                                                <tr>
                                                    <th className="service-name">
                                                        {getGroupTypeLabel(t, group)}
                                                    </th>
                                                    {showCourseColumn && (
                                                        <th className="course-checkbox-header">{t("plan.group.courseColumn")}</th>
                                                    )}
                                                    {showAppointmentColumns ? (
                                                        groupAppointmentNumbers.map((number) => {
                                                            // Две независимые подписи: номер приёма / день недели
                                                            // (режим группы) сверху крупно и тип приёма (этап
                                                            // программы) снизу бледно. Одна не подменяет другую.
                                                            const headerLabels =
                                                                getAppointmentHeaderLabels(
                                                                    number,
                                                                    appointmentHeaderType,
                                                                );
                                                            return (
                                                                <th key={number} className="appointment-header">
                                                                    <div className="appointment-header-cell">
                                                                        <span
                                                                            className="appointment-header-primary"
                                                                            title={headerLabels.primary}>
                                                                            {headerLabels.primary}
                                                                        </span>
                                                                        {headerLabels.secondary ? (
                                                                            <span
                                                                                className="appointment-header-secondary"
                                                                                title={headerLabels.secondary}>
                                                                                {headerLabels.secondary}
                                                                            </span>
                                                                        ) : null}
                                                                    </div>
                                                                </th>
                                                            );
                                                        })
                                                    ) : (
                                                        <th className="service-composition-header">{t("plan.group.composition")}</th>
                                                    )}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(() => {
                                                    const sortedItems = group.items || [];

                                                    const colSpan =
                                                        1 +
                                                        (showCourseColumn ? 1 : 0) +
                                                        (showAppointmentColumns ? groupAppointmentNumbers?.length || 0 : 1);

                                                    // Пустые курсы не отображаем: показываем заголовок только для курсов,
                                                    // у которых есть хотя бы одна услуга в текущей группе.
                                                    const courseIdsWithItems = new Set(
                                                        sortedItems
                                                            .map((it) => it?.id_treatment_plan_course?.value)
                                                            .filter((id) => id != null && String(id).trim() !== "" && String(id) !== "0"),
                                                    );

                                                    let lastCourseId = null;

                                                    return sortedItems.map((item) => {
                                                        const courseId = item?.id_treatment_plan_course?.value;
                                                        const isCourse = Boolean(courseId) && String(courseId) !== "0";
                                                        const rowStyle =
                                                            isCourse && courseIdToColor[courseId]
                                                                ? { backgroundColor: courseIdToColor[courseId] }
                                                                : undefined;

                                                        const courseNameRaw = isCourse
                                                            ? String(courseIdToName[courseId] ?? "").trim()
                                                            : "";
                                                        const courseName =
                                                            courseNameRaw && courseNameRaw !== "0" ? courseNameRaw : "";

                                                        const shouldShowCourseTitle =
                                                            !isWithoutAppointmentsGroup &&
                                                            isCourse &&
                                                            courseName &&
                                                            courseIdsWithItems.has(courseId) &&
                                                            courseId !== lastCourseId;
                                                        if (shouldShowCourseTitle) lastCourseId = courseId;

                                                        return (
                                                            <React.Fragment key={item?.id?.value}>
                                                                {shouldShowCourseTitle && (
                                                                    <tr className="course-title-row" style={rowStyle}>
                                                                        <td className="service-name" colSpan={colSpan}>
                                                                            <strong>{t("plan.group.program", { name: courseName })}</strong>
                                                                        </td>
                                                                    </tr>
                                                                )}

                                                                {/* Основная услуга */}
                                                                <tr className="main-service" style={rowStyle}>
                                                                    <td className="service-name">
                                                                        <div className="service-title">
                                                                            <div className="mb-1">
                                                                                {displayName(item)}
                                                                            </div>
                                                                        </div>
                                                                        {!isWithoutAppointmentsGroup && (
                                                                            <div className="service-composition">
                                                                                <div className="mb-1">
                                                                                    {item?.composition?.value}
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                        {(item?.subitems || []).length > 0 && (
                                                                            <div className="service-subitems">
                                                                                {(item.subitems || []).map((sub) => (
                                                                                    <div key={sub?.id?.value} className="service-subitem">
                                                                                        <span className="service-subitem__name">
                                                                                            {displayName(sub)}
                                                                                        </span>
                                                                                        {changed && (
                                                                                            <IconButton
                                                                                                className="service-subitem__delete"
                                                                                                icon="close"
                                                                                                onClick={() => onDeleteSubitem(sub?.id?.value)}
                                                                                                size="sm"
                                                                                                title={t("plan.group.deleteSubitem")}
                                                                                                variant="danger"
                                                                                            />
                                                                                        )}
                                                                                        {sub?.composition?.value ? (
                                                                                            <div className="service-subitem__composition">
                                                                                                {sub.composition.value}
                                                                                            </div>
                                                                                        ) : null}
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        )}
                                                                        <div className="service-price">
                                                                            <div className="mb-1">
                                                                                {isWithoutAppointmentsGroup
                                                                                    ? `${parseFloat(
                                                                                        item?.amount_base?.value || 0,
                                                                                    ).toLocaleString(locale)} ₽`
                                                                                    : `${getMarkedAppointmentsCountForItem(item)} X ${parseFloat(
                                                                                        (item?.amount_base_total?.value ?? item?.amount_base?.value) || 0,
                                                                                    ).toLocaleString(locale)} ₽`}
                                                                            </div>
                                                                        </div>

                                                                        {changed && (
                                                                            <div className="service-actions">
                                                                                <Button
                                                                                    onClick={() =>
                                                                                        onChangeService(
                                                                                            item?.id?.value,
                                                                                        )
                                                                                    }
                                                                                    size="sm"
                                                                                    variant="secondary">
                                                                                    {t("plan.group.change")}
                                                                                </Button>
                                                                                <Button
                                                                                    onClick={() =>
                                                                                        onAddSubitem(
                                                                                            group?.type?.value,
                                                                                            item?.id?.value,
                                                                                        )
                                                                                    }
                                                                                    size="sm"
                                                                                    variant="secondary">
                                                                                    {t("plan.group.addToComposition")}
                                                                                </Button>
                                                                                <IconButton
                                                                                    icon="trash"
                                                                                    onClick={() =>
                                                                                        onDeleteService(
                                                                                            item?.id?.value,
                                                                                        )
                                                                                    }
                                                                                    size="sm"
                                                                                    title={t("plan.group.delete")}
                                                                                    variant="danger"
                                                                                />
                                                                            </div>
                                                                        )}
                                                                    </td>
                                                                    {showCourseColumn && (
                                                                        <td className="course-checkbox-cell">
                                                                            {courseId && courseId !== "0" ? (
                                                                                <IconButton
                                                                                    icon="close"
                                                                                    onClick={() =>
                                                                                        onRemoveFromCourse(item?.id?.value)
                                                                                    }
                                                                                    size="sm"
                                                                                    title={t("plan.group.removeFromCourse")}
                                                                                    variant="secondary"
                                                                                />
                                                                            ) : (
                                                                                <input
                                                                                    type="checkbox"
                                                                                    checked={courseSelected.includes(item?.id?.value)}
                                                                                    onChange={() =>
                                                                                        onToggleCourse(item?.id?.value)
                                                                                    }
                                                                                />
                                                                            )}
                                                                        </td>
                                                                    )}
                                                                    {showAppointmentColumns ? (
                                                                        groupAppointmentNumbers.map((number) => (
                                                                            <td
                                                                                key={number}
                                                                                className="appointment-cell">
                                                                                {(() => {
                                                                                    const appointment = getAppointmentForItem(item, number);
                                                                                    const appointmentComment = String(
                                                                                        appointment?.comment?.value ?? "",
                                                                                    ).trim();
                                                                                    const appointmentStatus = renderAppointmentStatus(
                                                                                        item.appointments,
                                                                                        number,
                                                                                    );
                                                                                    const hasCommentOnFilledAppointment =
                                                                                        appointmentStatus === "filled" && Boolean(appointmentComment);

                                                                                    return (
                                                                                        <div className="appointment-cell-content">
                                                                                            {changed ? (
                                                                                                <>
                                                                                                    <span
                                                                                                        onClick={async () => {
                                                                                                            const response = await fetchPlan(
                                                                                                                {
                                                                                                                    id_plan: planId || 0,
                                                                                                                    id_group: group?.id?.value,
                                                                                                                    id_item: item?.id?.value,
                                                                                                                    id_appointment: number,
                                                                                                                    action: "appointment",
                                                                                                                    value: "check",
                                                                                                                    showFullLoading: false,
                                                                                                                }
                                                                                                            );
                                                                                                            if (isSuccessResponse(response)) {
                                                                                                                await logPlanChange("patient_plan_appointment_checked", {
                                                                                                                    idItem: item?.id?.value,
                                                                                                                    type: "appointment",
                                                                                                                    details: t("plan.log.serviceAppointment", {
                                                                                                                        service: item?.name?.value || item?.id?.value,
                                                                                                                        number,
                                                                                                                    }),
                                                                                                                });
                                                                                                            }
                                                                                                        }}
                                                                                                        className={`appointment-indicator ${appointmentStatus} ${hasCommentOnFilledAppointment ? "with-comment" : ""} changed`}
                                                                                                    />
                                                                                                    <button
                                                                                                        type="button"
                                                                                                        className="appointment-comment-button"
                                                                                                        onClick={() =>
                                                                                                            onOpenAppointmentComment(
                                                                                                                number,
                                                                                                                group?.id?.value,
                                                                                                                item?.id?.value,
                                                                                                                appointmentComment,
                                                                                                            )
                                                                                                        }>
                                                                                                        {t("plan.buttons.note")}
                                                                                                    </button>
                                                                                                </>
                                                                                            ) : (
                                                                                                <>
                                                                                                    <span
                                                                                                        className={`appointment-indicator ${appointmentStatus} ${hasCommentOnFilledAppointment ? "with-comment" : ""}`}
                                                                                                    />
                                                                                                    {appointmentComment && (
                                                                                                        <div
                                                                                                            className="appointment-comment-text"
                                                                                                            title={appointmentComment}
                                                                                                        >
                                                                                                            {appointmentComment}
                                                                                                        </div>
                                                                                                    )}
                                                                                                </>
                                                                                            )}
                                                                                        </div>
                                                                                    );
                                                                                })()}
                                                                            </td>
                                                                        ))
                                                                    ) : (
                                                                        <td className="service-composition-cell">
                                                                            <div className="mb-1">{item?.composition?.value || "—"}</div>
                                                                        </td>
                                                                    )}
                                                                </tr>
                                                            </React.Fragment>
                                                        );
                                                    });
                                                })()}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div className="no-services-name">
                                        {getGroupTypeLabel(t, group)}
                                    </div>
                                    <div className="no-services">{t("plan.group.noServicesInGroup")}</div>
                                </>
                            )}
                            {(changed || coursesMode) && (
                                <div className="btn-add-service">
                                    {coursesMode && (
                                        <Button
                                            onClick={onOpenAddCourse}
                                            size="sm"
                                            variant="secondary">
                                            {t("plan.buttons.addCourse")}
                                        </Button>
                                    )}
                                    {changed && (
                                        <div className="btn-add-service-dropdown">
                                            <Button
                                                aria-expanded={addServiceMenuGroupId === group?.id?.value}
                                                aria-haspopup="menu"
                                                onClick={() =>
                                                    setAddServiceMenuGroupId((prev) =>
                                                        prev === group?.id?.value ? null : group?.id?.value,
                                                    )
                                                }
                                                size="sm"
                                                variant="secondary">
                                                <span>{t("plan.buttons.addService")}</span>
                                                <IoChevronDownOutline
                                                    aria-hidden="true"
                                                    className={`btn-add-service-arrow${
                                                        addServiceMenuGroupId === group?.id?.value
                                                            ? " is-open"
                                                            : ""
                                                    }`}
                                                    size={16}
                                                />
                                            </Button>
                                            {addServiceMenuGroupId === group?.id?.value && (
                                                <div className="btn-add-service-menu" role="menu">
                                                    <button
                                                        type="button"
                                                        className="btn-add-service-item"
                                                        role="menuitem"
                                                        onClick={() => {
                                                            setAddServiceMenuGroupId(null);
                                                            onOpenAddServiceFromCatalog(
                                                                group?.type?.value,
                                                                group?.id?.value,
                                                            );
                                                        }}>
                                                        {t("plan.buttons.addServiceFromCatalog")}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn-add-service-item"
                                                        role="menuitem"
                                                        onClick={() => {
                                                            setAddServiceMenuGroupId(null);
                                                            onOpenAddCustomService(
                                                                group?.type?.value,
                                                                group?.id?.value,
                                                            );
                                                        }}>
                                                        {t("plan.buttons.addServiceNew")}
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                            {/* Итог по группе */}
                            <div className="group-summary">
                                <div className="group-prices">
                                    <div className="price-total">
                                        {t("plan.group.total")}
                                        {groupEffectiveTotal.toLocaleString(locale)}{" "}
                                        ₽
                                    </div>
                                    {/* Цветная индикация оплаты */}
                                    {(() => {
                                        const fact = parseFloat(group?.amount_fact?.value) || 0;

                                        let color = "#6c757d61";

                                        return (
                                            <div className="price-fact" style={{ color }}>
                                                {t("plan.group.paid", {
                                                    amount: fact.toLocaleString(locale),
                                                })}
                                            </div>
                                        );
                                    })()}
                                    <div className="price-base">
                                        <div>
                                            {(parseFloat(group?.amount_base?.value) || 0) >
                                            groupEffectiveTotal ? (
                                                <>
                                                    {t("plan.group.insteadOf")}
                                                    {parseFloat(
                                                        group.amount_base.value,
                                                    ).toLocaleString(locale)}{" "}
                                                    ₽
                                                </>
                                            ) : (
                                                false
                                            )}
                                        </div>
                                        <div>
                                            <>
                                                {t("plan.group.unpaid")}
                                                {parseFloat(
                                                    group.amount_fact.value < groupEffectiveTotal
                                                        ? groupEffectiveTotal -
                                                            group.amount_fact.value
                                                        : 0,
                                                ).toLocaleString(locale)}{" "}
                                                ₽
                                            </>
                                        </div>
                                    </div>

                                    <div className="price-reason-discount">
                                        {t("plan.group.discountReason")}
                                        {group.reason_discount?.value
                                            ? group.reason_discount?.value
                                            : "—"}
                                    </div>
                                    {!changed && (
                                        <div className="btn-add-paid">
                                            <Button
                                                onClick={() => onOpenPay(group.id.value)}
                                                size="sm"
                                                variant="secondary">
                                                {t("plan.buttons.addPayment")}
                                            </Button>
                                        </div>
                                    )}
                                </div>
                                {changed && (
                                    <div className="group-actions">
                                        <Button
                                            onClick={() => onDeleteGroup(group?.id?.value)}
                                            size="sm"
                                            variant="danger">
                                            {t("plan.buttons.deleteGroup")}
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
            {/* Подвал плана: слева — «Добавить группу», справа — «Удалить план». */}
            <div className="plan-footer-actions">
                {/* «Добавить группу» — сама кнопка раскрывает список направлений */}
                {onRequestAddGroup && onAddGroup ? (
                    <div
                        className={`plan-group-add${
                            groupMenuOpen ? " is-open" : ""
                        }`}>
                        <Button
                            icon="add"
                            iconPosition="left"
                            onClick={toggleGroupMenu}
                            size="md"
                            variant="primary">
                            <span className="plan-group-add__label">
                                {t("plan.buttons.addGroup")}
                                <IoChevronDownOutline
                                    size={14}
                                    className="plan-group-add__chev"
                                    aria-hidden="true"
                                />
                            </span>
                        </Button>
                        {groupMenuOpen ? (
                            <div className="plan-group-add__menu" role="menu">
                                {groupsLoading ? (
                                    <div className="plan-group-add__note">
                                        {t("plan.modals.groupsLoading")}
                                    </div>
                                ) : groupOptions.length ? (
                                    groupOptions.map((option) => (
                                        <button
                                            key={option.id}
                                            type="button"
                                            role="menuitem"
                                            className="plan-group-add__item"
                                            onClick={() =>
                                                handlePickGroup(option)
                                            }>
                                            {getGroupTypeLabel(t, option)}
                                        </button>
                                    ))
                                ) : (
                                    <div className="plan-group-add__note">
                                        {t("plan.modals.noGroupsAvailable")}
                                    </div>
                                )}
                            </div>
                        ) : null}
                    </div>
                ) : null}
                {onDeletePlan ? (
                    <Button
                        onClick={onDeletePlan}
                        size="md"
                        variant="danger">
                        {deletePlanLabel || t("plan.buttons.deletePlan")}
                    </Button>
                ) : null}
            </div>
        </div>
    );
}
